import { BrowserRouter, Routes, Route, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { 
  LayoutDashboard, 
  ShoppingCart, 
  Receipt, 
  Package, 
  FileText,
  Menu,
  X,
  TrendingUp
} from "lucide-react";
import "./App.css";

// Pages
import Dashboard from "./pages/Dashboard";
import Sales from "./pages/Sales";
import Costs from "./pages/Costs";
import Products from "./pages/Products";
import Reports from "./pages/Reports";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sidebar component
const Sidebar = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  
  const navItems = [
    { path: "/", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/sales", icon: ShoppingCart, label: "Sales" },
    { path: "/costs", icon: Receipt, label: "Costs" },
    { path: "/products", icon: Package, label: "Products" },
    { path: "/reports", icon: FileText, label: "Reports" },
  ];

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 lg:hidden cursor-pointer"
          onClick={() => setIsOpen(false)}
          onTouchEnd={() => setIsOpen(false)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Escape' && setIsOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}
      
      {/* Sidebar */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-stone-200 transform transition-transform duration-300 ease-out lg:transform-none ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-stone-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-600 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-semibold text-stone-900 text-lg tracking-tight">Baker's Insight</h1>
                <p className="text-xs text-stone-500">Profit Tracker</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={({ isActive }) => 
                  `sidebar-link ${isActive || (item.path === "/" && location.pathname === "/") ? 'active' : ''}`
                }
                data-testid={`nav-${item.label.toLowerCase()}`}
                end={item.path === "/"}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-stone-100">
            <p className="text-xs text-stone-400 text-center">
              © 2026 Baker's Insight
            </p>
          </div>
        </div>
      </aside>
    </>
  );
};

// Top bar component
const TopBar = ({ setIsSidebarOpen }) => {
  return (
    <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-sm border-b border-stone-100 px-6 py-4 lg:hidden">
      <div className="flex items-center justify-between">
        <button
          onClick={() => setIsSidebarOpen(true)}
          className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-5 h-5 text-stone-600" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-amber-600 rounded-lg flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-stone-900">Baker's Insight</span>
        </div>
        <div className="w-9" /> {/* Spacer for centering */}
      </div>
    </header>
  );
};

// Layout wrapper
const Layout = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  return (
    <div className="flex min-h-screen bg-[#FDFBF7]">
      <Sidebar isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar setIsSidebarOpen={setIsSidebarOpen} />
        <main className="flex-1 p-6 md:p-8 lg:p-10">
          {children}
        </main>
      </div>
    </div>
  );
};

function App() {
  // Seed products on first load
  useEffect(() => {
    const seedProducts = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch (e) {
        // Silently fail if already seeded
      }
    };
    seedProducts();
  }, []);

  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <Layout>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/sales" element={<Sales />} />
          <Route path="/costs" element={<Costs />} />
          <Route path="/products" element={<Products />} />
          <Route path="/reports" element={<Reports />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}

export default App;
